import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Target as TargetIcon, TrendingUp, TrendingDown, Download, FileText, FileSpreadsheet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TargetManagement() {
  const [performance, setPerformance] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [open, setOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('this_month');
  const [formData, setFormData] = useState({
    recruiter_id: '',
    cv_target: '',
    interview_target: '',
    closure_target: '',
    period: 'this_month'
  });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkData, setBulkData] = useState({ cv_target: '', interview_target: '', closure_target: '', period: 'this_month' });

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) setUser(JSON.parse(userData));
  }, []);

  useEffect(() => {
    fetchPerformance();
    if (user && user.role === 'admin') fetchRecruiters();
  }, [user, selectedPeriod]);

  const fetchPerformance = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/targets/performance?period=${selectedPeriod}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPerformance(response.data);
    } catch (error) {
      toast.error('Failed to fetch performance');
    }
  };

  const fetchRecruiters = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecruiters(response.data.filter(u => u.role === 'recruiter'));
    } catch (error) {
      /* silent */
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/targets`, {
        ...formData,
        cv_target: parseInt(formData.cv_target),
        interview_target: parseInt(formData.interview_target),
        closure_target: parseInt(formData.closure_target)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Target set successfully');
      setOpen(false);
      setFormData({ recruiter_id: '', cv_target: '', interview_target: '', closure_target: '', period: 'this_month' });
      fetchPerformance();
    } catch (error) {
      toast.error('Failed to set target');
    }
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/targets/bulk`, {
        cv_target: parseInt(bulkData.cv_target) || 0,
        interview_target: parseInt(bulkData.interview_target) || 0,
        closure_target: parseInt(bulkData.closure_target) || 0,
        period: bulkData.period,
        recruiter_ids: []  // empty = all recruiters
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message || 'Bulk targets set');
      setBulkOpen(false);
      setBulkData({ cv_target: '', interview_target: '', closure_target: '', period: 'this_month' });
      fetchPerformance();
    } catch (error) {
      toast.error('Failed to set bulk targets');
    }
  };

  const handleExport = async (format) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/export/targets?format=${format}&period=${selectedPeriod}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `targets.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  const isAdmin = user && user.role === 'admin';
  const canExport = user && (user.role === 'admin' || user.role === 'team_leader');

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-blue-900 mb-2" data-testid="targets-title" style={{ fontFamily: 'Manrope, sans-serif' }}>Target Management</h1>
          <p className="text-slate-600">Set and track recruiter performance targets</p>
        </div>
        <div className="flex gap-3">
          <select
            data-testid="period-filter-select"
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="h-10 rounded border border-blue-200 bg-white px-4 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-600"
          >
            <option value="this_week">This Week</option>
            <option value="this_month">This Month</option>
            <option value="this_year">This Year</option>
          </select>
          {canExport && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="export-targets-button" variant="outline" className="border-blue-200 text-blue-700 hover:bg-blue-50">
                  <Download size={16} className="mr-2" />
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem data-testid="export-targets-excel" onClick={() => handleExport('excel')}>
                  <FileSpreadsheet size={16} className="mr-2" />
                  Export as Excel
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="export-targets-pdf" onClick={() => handleExport('pdf')}>
                  <FileText size={16} className="mr-2" />
                  Export as PDF
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {isAdmin && (
            <>
            <Button data-testid="set-bulk-target-button" variant="outline" onClick={() => setBulkOpen(true)}
              className="border-indigo-300 text-indigo-700 hover:bg-indigo-50 font-bold">
              <TargetIcon size={16} className="mr-2" />
              Bulk Set All
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="set-target-button" className="bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">
                  <Plus size={16} className="mr-2" />
                  Set Target
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold text-blue-900">Set New Target</DialogTitle>
                  <DialogDescription>Set performance targets for a recruiter for the selected period.</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Recruiter</Label>
                    <select
                      data-testid="target-recruiter-select"
                      value={formData.recruiter_id}
                      onChange={(e) => setFormData({ ...formData, recruiter_id: e.target.value })}
                      required
                      className="mt-2 flex h-10 w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-blue-600"
                    >
                      <option value="">Select recruiter</option>
                      {recruiters.map(rec => (
                        <option key={rec.id} value={rec.id}>{rec.name} {rec.team_name ? `(${rec.team_name})` : ''}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Period</Label>
                    <select
                      data-testid="target-period-select"
                      value={formData.period}
                      onChange={(e) => setFormData({ ...formData, period: e.target.value })}
                      required
                      className="mt-2 flex h-10 w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-blue-600"
                    >
                      <option value="this_week">This Week</option>
                      <option value="this_month">This Month</option>
                      <option value="this_year">This Year</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">CV Target</Label>
                      <Input
                        data-testid="cv-target-input"
                        type="number"
                        value={formData.cv_target}
                        onChange={(e) => setFormData({ ...formData, cv_target: e.target.value })}
                        required
                        className="mt-2 rounded border-blue-200"
                        placeholder="10"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Interview Target</Label>
                      <Input
                        data-testid="interview-target-input"
                        type="number"
                        value={formData.interview_target}
                        onChange={(e) => setFormData({ ...formData, interview_target: e.target.value })}
                        required
                        className="mt-2 rounded border-blue-200"
                        placeholder="5"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Closure Target</Label>
                      <Input
                        data-testid="closure-target-input"
                        type="number"
                        value={formData.closure_target}
                        onChange={(e) => setFormData({ ...formData, closure_target: e.target.value })}
                        required
                        className="mt-2 rounded border-blue-200"
                        placeholder="2"
                      />
                    </div>
                  </div>
                  <Button data-testid="submit-target-button" type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">
                    Set Target
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            {/* Bulk Set Dialog */}
            <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
              <DialogContent className="rounded">
                <DialogHeader>
                  <DialogTitle className="text-xl font-bold text-blue-900">Bulk Set Targets — All Recruiters</DialogTitle>
                  <DialogDescription>Apply the same targets to all recruiters at once. Existing targets for the period will be updated.</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleBulkSubmit} className="space-y-4 mt-4">
                  <div>
                    <Label className="text-xs font-bold uppercase text-blue-900">Period</Label>
                    <select value={bulkData.period} onChange={e => setBulkData({ ...bulkData, period: e.target.value })}
                      className="mt-1 flex h-10 w-full rounded border border-blue-200 bg-white px-3 text-sm" data-testid="bulk-period-select">
                      <option value="this_week">This Week</option>
                      <option value="this_month">This Month</option>
                      <option value="this_quarter">This Quarter</option>
                      <option value="this_year">This Year</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs font-bold uppercase text-blue-900">CV Target</Label>
                      <Input type="number" value={bulkData.cv_target} onChange={e => setBulkData({ ...bulkData, cv_target: e.target.value })} required placeholder="20" className="mt-1 rounded border-blue-200" data-testid="bulk-cv-target" />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase text-blue-900">Interview Target</Label>
                      <Input type="number" value={bulkData.interview_target} onChange={e => setBulkData({ ...bulkData, interview_target: e.target.value })} required placeholder="5" className="mt-1 rounded border-blue-200" data-testid="bulk-interview-target" />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase text-blue-900">Closure Target</Label>
                      <Input type="number" value={bulkData.closure_target} onChange={e => setBulkData({ ...bulkData, closure_target: e.target.value })} required placeholder="2" className="mt-1 rounded border-blue-200" data-testid="bulk-closure-target" />
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 bg-blue-50 border border-blue-200 rounded p-2">This will set/update targets for <strong>all recruiters</strong> in your organization for the selected period.</p>
                  <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded font-bold" data-testid="bulk-submit-btn">
                    Apply to All Recruiters
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {performance.length === 0 ? (
          <div className="col-span-2 bg-white border border-blue-100 rounded p-12 text-center">
            <TargetIcon size={48} className="mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500" data-testid="no-targets-message">No targets set for this period. {isAdmin && 'Set targets to start tracking performance.'}</p>
          </div>
        ) : (
          performance.map((perf) => {
            const isAggregate = perf.target_id === 'team_aggregate';
            return (
            <div key={perf.target_id} data-testid={`target-card-${perf.target_id}`}
              className={`${isAggregate ? 'lg:col-span-2 bg-gradient-to-r from-blue-50 to-cyan-50 border-2 border-blue-300' : 'bg-white border border-blue-100'} rounded p-6 shadow-sm hover:shadow-md transition-shadow`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className={`text-xl font-bold ${isAggregate ? 'text-blue-800' : 'text-blue-900'}`}>
                    {isAggregate && <span className="mr-2">&#9632;</span>}
                    {perf.recruiter_name}
                  </h3>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Period: {perf.period?.replace('_', ' ')}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-extrabold text-blue-900">
                    {Math.round((perf.cv_achievement + perf.interview_achievement + perf.closure_achievement) / 3)}%
                  </p>
                  <p className="text-xs text-slate-500">Overall</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-semibold text-slate-700">CVs Submitted</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-blue-900">{perf.cv_actual} / {perf.cv_target}</span>
                      {perf.cv_achievement >= 100 ?
                        <TrendingUp size={16} className="text-emerald-600" /> :
                        <TrendingDown size={16} className="text-amber-600" />
                      }
                    </div>
                  </div>
                  <Progress value={Math.min(perf.cv_achievement, 100)} className="h-2" />
                  <p className="text-xs text-right text-slate-500 mt-1">{perf.cv_achievement}%</p>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-semibold text-slate-700">Interviews Conducted</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-blue-900">{perf.interview_actual} / {perf.interview_target}</span>
                      {perf.interview_achievement >= 100 ?
                        <TrendingUp size={16} className="text-emerald-600" /> :
                        <TrendingDown size={16} className="text-amber-600" />
                      }
                    </div>
                  </div>
                  <Progress value={Math.min(perf.interview_achievement, 100)} className="h-2" />
                  <p className="text-xs text-right text-slate-500 mt-1">{perf.interview_achievement}%</p>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-semibold text-slate-700">Closures Achieved</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-blue-900">{perf.closure_actual} / {perf.closure_target}</span>
                      {perf.closure_achievement >= 100 ?
                        <TrendingUp size={16} className="text-emerald-600" /> :
                        <TrendingDown size={16} className="text-amber-600" />
                      }
                    </div>
                  </div>
                  <Progress value={Math.min(perf.closure_achievement, 100)} className="h-2" />
                  <p className="text-xs text-right text-slate-500 mt-1">{perf.closure_achievement}%</p>
                </div>
              </div>
            </div>
            );
          })
        )}
      </div>
    </div>
  );
}
